import { NamedObjectDisplay } from "./NamedObjectDisplay";
import { RemoveArsItemButton } from "./RemoveArsItemButton";
import { WhereClauseManager } from "./WhereClauseManager";

/**
 * Props for the DataSubsetControl component.
 */
export interface DataSubsetControlProps {
  /** RFC 6901 JSON Pointer path to the DataSubset in ARSContext state */
  dataPath: string;
  /** Can rename the object */
  canRename?: boolean;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * A component for managing DataSubset entities with full CRUD capabilities.
 *
 * This component provides:
 * - NamedObjectDisplay for editing name, description, and label
 * - WhereClauseManager for managing where clause conditions
 * - RemoveButton for deleting the data subset
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the DataSubset in ARSContext state.
 * @param props.canRename - Optional flag to allow renaming the object.
 * @param props.disabled - Optional flag to disable editing controls.
 * @returns A React functional component rendering the data subset editor.
 */
export const DataSubsetControl = ({
  dataPath,
  canRename = false,
  disabled = false,
}: DataSubsetControlProps): React.ReactElement => {
  return (
    <div className="border rounded p-3 mb-3 position-relative">
      {canRename && (
        <div className="position-absolute top-0 end-0 m-2">
          <RemoveArsItemButton
            dataPath={dataPath}
            disabled={disabled}
          />
        </div>
      )}

      <NamedObjectDisplay
        dataPath={dataPath}
        canRename={canRename}
        disabled={disabled}
      />

      <WhereClauseManager
        dataPath={dataPath}
        disabled={disabled}
      />
    </div>
  );
};

DataSubsetControl.displayName = "DataSubsetControl";
